import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import {
  Box,
  Chip,
  CircularProgress,
  colors,
  Drawer,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Product } from "../../../models/Products/Product";
import { ProductVariant } from "../../../models/Products/ProductVariant";
import { FilterValue } from "../../../models/Filters/FilterValue";
import AppButton from "../common/buttons/AppButton";
import CustomTypography from "../common/form/CustomTypography";
import AppSearchDropdownMulti from "../common/filter/AppSearchDropdownMulti";
import { OrderProductsVariantsStore } from "../../../stores/OrderProductsVariantsStore";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";

export interface ProductVariantRowInput {
  size: string;
  colorName: string;
  hexCode: string;
  quantity: number;
  sku?: string;
}

interface ProductVariantRow extends ProductVariantRowInput {
  id: string;
  variantId?: number;
  isEditing: boolean;
  createdByAdminId?: number | null;
  createdByAdminName?: string | null;
  createdByUserId?: number | null;
  createdByUserName?: string | null;
  updatedByAdminId?: number | null;
  updatedByAdminName?: string | null;
  updatedByUserId?: number | null;
  updatedByUserName?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
}

export interface VariantApiPayload {
  product_id: number | string | null;
  size: string;
  color: string | null;
  hex_code: string | null;
  quantity: number;
  sku: string | null;
}

interface OrderProductVariantsDrawerProps {
  open: boolean;
  product_id?: number | string | null;
  order_id?: number | string | null;
  total_quantity: number;
  productDetails?: Product | null;
  isLoading?: boolean;
  initialRows?: ProductVariantRowInput[];
  onClose: () => void;
  onSubmit: (rows: ProductVariantRowInput[]) => void;
  onSaveVariant?: (payload: VariantApiPayload) => void;
  onDeleteVariant?: (payload: VariantApiPayload) => void;
}

const orderProductVariantStore = new OrderProductsVariantsStore();

const VARIANT_DRAWER_Z_OFFSET = 2000;

const OrderProductVariantsDrawer: React.FC<OrderProductVariantsDrawerProps> = ({
  open,
  product_id,
  order_id,
  total_quantity,
  productDetails,
  isLoading = false,
  initialRows,
  onClose,
  onSaveVariant,
  onDeleteVariant,
}) => {
  const theme = useTheme();
  const { userStore, orderStore, productStore } = useStores();

  const [rows, setRows] = useState<ProductVariantRow[]>([]);
  const [savedRowsSnapshot, setSavedRowsSnapshot] = useState<
    Record<string, ProductVariantRowInput>
  >({});
  const [hexDrafts, setHexDrafts] = useState<Record<string, string>>({});
  const [copiedSkuRowId, setCopiedSkuRowId] = useState<string | null>(null);
  const [variantsFetchLoading, setVariantsFetchLoading] = useState(false);
  const [savingRowIds, setSavingRowIds] = useState<Set<string>>(new Set());
  const [deletingRowIds, setDeletingRowIds] = useState<Set<string>>(new Set());
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [savingErrors, setSavingErrors] = useState<Record<string, string>>({});
  const [deletingErrors, setDeletingErrors] = useState<Record<string, string>>(
    {},
  );
  const [resolvedProductDetails, setResolvedProductDetails] =
    useState<Product | null>(productDetails ?? null);
  const [productDetailsLoading, setProductDetailsLoading] = useState(false);

  useEffect(() => {
    if (!open || !product_id) return;

    if (productDetails) {
      setResolvedProductDetails(productDetails);
      setProductDetailsLoading(false);
      return;
    }

    setProductDetailsLoading(true);
    productStore
      .getProductDetailsById(Number(product_id))
      .then((details) => {
        setResolvedProductDetails(details || null);
      })
      .catch(() => {
        setResolvedProductDetails(null);
      })
      .finally(() => setProductDetailsLoading(false));
  }, [open, product_id, productDetails, productStore]);

  useEffect(() => {
    if (!open || !product_id || !order_id) return;

    setRows([]);
    setSavedRowsSnapshot({});
    setHexDrafts({});
    setFetchError(null);
    setVariantsFetchLoading(true);

    orderProductVariantStore
      .fetchVariantsByOrderIdAndProductId(Number(order_id), Number(product_id))
      .then(() => {
        const preparedRows: ProductVariantRow[] =
          orderProductVariantStore.variants.map((v, index) => ({
            id: `${Date.now()}-${index}-${Math.random()}`,
            variantId: v.id,
            size: v.size,
            colorName: v.color_label || "",
            hexCode: v.color_hex || "",
            quantity: v.quantity > 0 ? v.quantity : 1,
            sku: v.sku,
            isEditing: false,
            createdByAdminId: v.created_by_admin_id,
            createdByAdminName: v.created_by_admin_name,
            createdByUserId: v.created_by_user_id,
            createdByUserName: v.created_by_user_name,
            updatedByAdminId: v.updated_by_admin_id,
            updatedByAdminName: v.updated_by_admin_name,
            updatedByUserId: v.updated_by_user_id,
            updatedByUserName: v.updated_by_user_name,
            createdAt: v.created_at,
            updatedAt: v.updated_at,
          }));
        setRows(preparedRows);

        const snapshot: Record<string, ProductVariantRowInput> = {};
        preparedRows.forEach((row) => {
          snapshot[row.id] = {
            size: row.size,
            colorName: row.colorName,
            hexCode: row.hexCode,
            quantity: row.quantity,
          };
        });
        setSavedRowsSnapshot(snapshot);

        const drafts: Record<string, string> = {};
        preparedRows.forEach((row) => {
          drafts[row.id] = (row.hexCode || "").toUpperCase();
        });
        setHexDrafts(drafts);
      })
      .catch((error: any) => {
        setFetchError(
          error?.response?.data?.message ||
            "Failed to load variants. Please try again.",
        );
      })
      .finally(() => setVariantsFetchLoading(false));
  }, [open, product_id, order_id]);

  const sizeOptions = useMemo(() => {
    return (resolvedProductDetails?.variants || [])
      .map((variant: ProductVariant) => variant.title || variant.value)
      .filter(Boolean)
      .map((item) => String(item).trim())
      .filter((item, idx, arr) => arr.indexOf(item) === idx);
  }, [resolvedProductDetails]);

  const sizeOptionList = useMemo<FilterValue[]>(
    () =>
      sizeOptions.map((size, index) => ({
        id: String(index + 1),
        value: size,
      })),
    [sizeOptions],
  );

  const sanitizeHexInput = (value: string) => {
    const raw = value.trim();
    if (!raw) return "";
    const withHash = raw.startsWith("#") ? raw : `#${raw}`;
    const cleaned = withHash
      .slice(1)
      .replace(/[^0-9a-fA-F]/g, "")
      .slice(0, 6);
    return `#${cleaned}`.toUpperCase();
  };

  const isFullHex = (value: string) => /^#[0-9A-Fa-f]{6}$/.test(value);

  const generateSKU = ({
    productId,
    orderId,
    size,
    color,
  }: {
    productId: number | string;
    orderId: number | string;
    size: string;
    color?: string;
  }) => {
    const normalize = (val: string | number) =>
      String(val)
        .trim()
        .toUpperCase()
        .replace(/\s+/g, "-")
        .replace(/[^A-Z0-9-]/g, "");

    const parts = [
      "SE",
      normalize(orderId),
      normalize(productId),
      normalize(size),
    ];
    if (color) parts.push(normalize(color));
    return parts.join("-");
  };

  const getRowSku = (row: ProductVariantRow) => {
    if (!product_id || !order_id || !row.size || !row.size.trim()) return "";
    return generateSKU({
      productId: product_id,
      orderId: order_id,
      size: row.size,
      color: row.colorName?.trim() || undefined,
    });
  };

  const isDuplicateSku = (rowId: string) => {
    const current = rows.find((r) => r.id === rowId);
    if (!current) return false;
    const currentSku = getRowSku(current);
    if (!currentSku) return false;
    return rows.filter((r) => getRowSku(r) === currentSku).length > 1;
  };

  useEffect(() => {
    if (sizeOptions.length !== 1) return;
    const onlySize = sizeOptions[0];
    setRows((prev) =>
      prev.map((row) =>
        row.isEditing && !row.size.trim()
          ? { ...row, size: onlySize, quantity: total_quantity }
          : row,
      ),
    );
  }, [sizeOptions, total_quantity]);

  const handleAddVariantRow = () => {
    const rowId = `${Date.now()}-${Math.random()}`;
    setRows((prev) => [
      ...prev,
      {
        id: rowId,
        size: sizeOptions.length === 1 ? sizeOptions[0] : "",
        colorName: "",
        hexCode: "",
        quantity: sizeOptions.length === 1 ? total_quantity : 1,
        isEditing: true,
      },
    ]);
    setHexDrafts((prev) => ({ ...prev, [rowId]: "" }));
  };

  const handleEditRow = (rowId: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, isEditing: true } : row)),
    );
  };

  const handleRowChange = (
    rowId: string,
    key: keyof ProductVariantRowInput,
    value: string | number,
  ) => {
    setRows((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, [key]: value } : row)),
    );
    setSavingErrors((prev) => {
      if (!prev[rowId]) return prev;
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const handleTickAction = async (rowId: string) => {
    const targetRow = rows.find((row) => row.id === rowId);
    if (!targetRow || !order_id || !product_id) return;

    const sku = getRowSku(targetRow);
    const variantPayload = {
      product_id: Number(product_id),
      sku,
      size: targetRow.size.trim(),
      color_label: targetRow.colorName.trim() || null,
      color_hex: targetRow.hexCode || null,
      quantity: targetRow.quantity,
    };

    setSavingRowIds((prev) => new Set(prev).add(rowId));
    setSavingErrors((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    try {
      let resolvedVariantId: number | undefined = targetRow.variantId;

      if (targetRow.variantId) {
        await orderProductVariantStore.updateVariant(
          Number(order_id),
          targetRow.variantId,
          variantPayload,
        );
      } else {
        await orderProductVariantStore.addVariant(
          Number(order_id),
          variantPayload,
        );
        const savedVariant = orderProductVariantStore.variants.find(
          (v) => v.sku === sku && v.product_id === Number(product_id),
        );
        resolvedVariantId = savedVariant?.id;
      }

      setRows((prev) =>
        prev.map((row) =>
          row.id === rowId
            ? { ...row, isEditing: false, variantId: resolvedVariantId }
            : row,
        ),
      );
      setSavedRowsSnapshot((prev) => ({
        ...prev,
        [rowId]: {
          size: targetRow.size,
          colorName: targetRow.colorName,
          hexCode: targetRow.hexCode,
          quantity: targetRow.quantity,
        },
      }));
      setHexDrafts((prev) => ({
        ...prev,
        [rowId]: (targetRow.hexCode || "").toUpperCase(),
      }));

      if (onSaveVariant) {
        onSaveVariant({
          product_id: Number(product_id),
          size: targetRow.size.trim(),
          color: targetRow.colorName.trim() || null,
          hex_code: targetRow.hexCode || null,
          quantity: targetRow.quantity,
          sku: sku || null,
        });
      }
    } catch (error: any) {
      setSavingErrors((prev) => ({
        ...prev,
        [rowId]:
          error?.response?.data?.message ||
          (targetRow.variantId
            ? "Failed to update variant. Please try again."
            : "Failed to save variant. Please try again."),
      }));
    } finally {
      setSavingRowIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }
  };

  const handleCopySku = async (rowId: string) => {
    const targetRow = rows.find((row) => row.id === rowId);
    if (!targetRow) return;
    const sku = getRowSku(targetRow);
    if (!sku) return;
    try {
      await navigator.clipboard.writeText(sku);
      setCopiedSkuRowId(rowId);
      setTimeout(
        () => setCopiedSkuRowId((prev) => (prev === rowId ? null : prev)),
        1200,
      );
    } catch (_error) {}
  };

  const handleCancelEdit = (rowId: string) => {
    const snapshot = savedRowsSnapshot[rowId];
    setRows((prev) => {
      if (!snapshot) return prev.filter((row) => row.id !== rowId);
      return prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              size: snapshot.size,
              colorName: snapshot.colorName,
              hexCode: snapshot.hexCode,
              quantity: snapshot.quantity,
              isEditing: false,
            }
          : row,
      );
    });
    setHexDrafts((prev) => {
      const next = { ...prev };
      if (!snapshot) {
        delete next[rowId];
      } else {
        next[rowId] = (snapshot.hexCode || "").toUpperCase();
      }
      return next;
    });
  };

  const removeRowFromState = (rowId: string) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId));
    setSavedRowsSnapshot((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setHexDrafts((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const handleDeleteRow = async (rowId: string) => {
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    if (row.variantId && order_id) {
      setDeletingRowIds((prev) => new Set(prev).add(rowId));
      setDeletingErrors((prev) => {
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
      try {
        await orderProductVariantStore.deleteVariant(
          Number(order_id),
          row.variantId,
        );
        if (onDeleteVariant) {
          onDeleteVariant({
            product_id: product_id ?? null,
            size: row.size.trim(),
            color: row.colorName.trim() || null,
            hex_code: row.hexCode || null,
            quantity: row.quantity,
            sku: getRowSku(row) || null,
          });
        }
      } catch (error: any) {
        setDeletingErrors((prev) => ({
          ...prev,
          [rowId]:
            error?.response?.data?.message ||
            "Failed to delete variant. Please try again.",
        }));
        setDeletingRowIds((prev) => {
          const next = new Set(prev);
          next.delete(rowId);
          return next;
        });
        return;
      }
      setDeletingRowIds((prev) => {
        const next = new Set(prev);
        next.delete(rowId);
        return next;
      });
    }

    removeRowFromState(rowId);
  };

  const handleHexTextChange = (rowId: string, input: string) => {
    const sanitized = sanitizeHexInput(input);
    setHexDrafts((prev) => ({ ...prev, [rowId]: sanitized }));
    if (sanitized === "") {
      handleRowChange(rowId, "hexCode", "");
      return;
    }
    if (isFullHex(sanitized)) {
      handleRowChange(rowId, "hexCode", sanitized.toLowerCase());
    }
  };

  const handleHexTextBlur = (rowId: string) => {
    const draft = hexDrafts[rowId] || "";
    if (draft === "") {
      handleRowChange(rowId, "hexCode", "");
      return;
    }
    if (isFullHex(draft)) {
      setHexDrafts((prev) => ({ ...prev, [rowId]: draft }));
      handleRowChange(rowId, "hexCode", draft.toLowerCase());
      return;
    }
    const rowHex = rows.find((row) => row.id === rowId)?.hexCode || "";
    setHexDrafts((prev) => ({ ...prev, [rowId]: rowHex.toUpperCase() }));
  };

  const totalVariants = rows.filter((row) => !row.isEditing).length;

  const hasEditPermissions = (): boolean => {
    if (orderStore.orderBasicDetails?.project_id) {
      return userStore.hasPermission([
        EmployeeRoles.EDIT_ORDER_AFTER_PROJECT_CREATION,
      ]);
    }

    return userStore.hasPermission([
      EmployeeRoles.CREATE_ORDER,
      EmployeeRoles.EDIT_ORDER,
    ]);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
        style: { zIndex: 1200 + VARIANT_DRAWER_Z_OFFSET },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 600, md: 680 },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : "#F8FAFC",
          boxShadow:
            "0 24px 80px rgba(15, 23, 42, 0.25), 0 6px 20px rgba(15, 23, 42, 0.12)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.75,
          py: 2.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E2E8F0",
          backgroundColor: "#FFFFFF",
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <Box
          sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: 1 }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              backgroundColor: "#EEF2FF",
              color: "#4F46E5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <CustomTypography
              variant="heading6"
              fontWeight={700}
              style={{ marginBottom: "4px" }}
            >
              Product Variants
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={theme.palette.customColors.greyTextColor}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: { xs: "76vw", sm: 460 },
              }}
            >
              {resolvedProductDetails?.name || "-"}
            </CustomTypography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#94A3B8",
            "&:hover": { backgroundColor: "#F1F5F9", color: "#334155" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {isLoading || productDetailsLoading || variantsFetchLoading ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {/* Toolbar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomTypography
                variant="body2"
                fontWeight={700}
                color="#334155"
              >
                Variants
              </CustomTypography>
              <Chip
                label={totalVariants}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  backgroundColor: "#EEF2FF",
                  color: "#4F46E5",
                  "& .MuiChip-label": { px: 1 },
                }}
              />
            </Box>
            {hasEditPermissions() && (
              <AppButton
                label="Add Variant"
                variant="secondary"
                startIcon={<AddIcon />}
                onClick={handleAddVariantRow}
                customStyles={{
                  minWidth: "132px",
                  minHeight: "36px",
                  borderRadius: "10px",
                  fontSize: "0.78rem",
                  backgroundColor: "#FDF2F8",
                  color: "#DB2777",
                  border: "1px solid #FBCFE8",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#FCE7F3",
                    border: "1px solid #F9A8D4",
                    boxShadow: "none",
                  },
                }}
              />
            )}
          </Box>

          {/* Fetch error */}
          {fetchError && (
            <Box
              sx={{
                mb: 2,
                px: 1.5,
                py: 1.25,
                backgroundColor: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CustomTypography
                variant="body3"
                sx={{ color: "#DC2626", flex: 1, fontWeight: 500 }}
              >
                {fetchError}
              </CustomTypography>
            </Box>
          )}

          {/* Variant cards */}
          {rows.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                gap: 1,
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 36, color: "#CBD5E1" }} />
              <CustomTypography variant="body2" color="#94A3B8">
                No variants yet. Click Add Variant to get started.
              </CustomTypography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {rows.map((row) =>
                row.isEditing ? (
                  <EditVariantCard
                    key={row.id}
                    row={row}
                    sizeOptionList={sizeOptionList}
                    hexDraft={
                      hexDrafts[row.id] ?? (row.hexCode || "").toUpperCase()
                    }
                    sku={getRowSku(row)}
                    isDuplicate={isDuplicateSku(row.id)}
                    isSaving={savingRowIds.has(row.id)}
                    saveError={savingErrors[row.id]}
                    isExistingVariant={!!row.variantId}
                    isFullHex={isFullHex}
                    hasEditPermissions={hasEditPermissions()}
                    onRowChange={handleRowChange}
                    onHexTextChange={handleHexTextChange}
                    onHexTextBlur={handleHexTextBlur}
                    onHexPickerChange={(rowId, hex) => {
                      handleRowChange(rowId, "hexCode", hex.toLowerCase());
                      setHexDrafts((prev) => ({
                        ...prev,
                        [rowId]: hex.toUpperCase(),
                      }));
                    }}
                    onSave={handleTickAction}
                    onCancel={handleCancelEdit}
                  />
                ) : (
                  <ViewVariantCard
                    key={row.id}
                    row={row}
                    sku={row.sku || getRowSku(row)}
                    isDuplicate={isDuplicateSku(row.id)}
                    isCopied={copiedSkuRowId === row.id}
                    isDeleting={deletingRowIds.has(row.id)}
                    deleteError={deletingErrors[row.id]}
                    hasEditPermissions={hasEditPermissions()}
                    onEdit={handleEditRow}
                    onDelete={handleDeleteRow}
                    onCopySku={handleCopySku}
                  />
                ),
              )}
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

/* ─── View Mode Card ─────────────────────────────────────────────────── */

const formatAuditDate = (date: Date | null | undefined): string => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

interface ViewVariantCardProps {
  row: {
    id: string;
    size: string;
    colorName: string;
    hexCode: string;
    quantity: number;
    createdByAdminName?: string | null;
    createdByUserName?: string | null;
    updatedByAdminName?: string | null;
    updatedByUserName?: string | null;
    createdAt?: Date | null;
    updatedAt?: Date | null;
  };
  sku: string;
  isDuplicate: boolean;
  isCopied: boolean;
  isDeleting: boolean;
  deleteError?: string;
  hasEditPermissions: boolean;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onCopySku: (id: string) => void;
}

const ViewVariantCard: React.FC<ViewVariantCardProps> = ({
  row,
  sku,
  isDuplicate,
  isCopied,
  isDeleting,
  deleteError,
  hasEditPermissions,
  onEdit,
  onDelete,
  onCopySku,
}) => {
  const hasColor = !!row.hexCode && !!row.colorName;

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        border: "1px solid #E2E8F0",
        borderLeft: `3px solid ${hasColor ? row.hexCode : "#6366F1"}`,
        borderRadius: "10px",
        overflow: "hidden",
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: "0 2px 12px rgba(15,23,42,0.08)" },
      }}
    >
      {/* Row 1: Size + Quantity + Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          pt: 1.5,
          pb: 0.75,
          gap: 1.5,
        }}
      >
        <Chip
          label={row.size?.toUpperCase() || "–"}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.8rem",
            backgroundColor: "#EEF2FF",
            color: "#4F46E5",
            borderRadius: "6px",
            height: 26,
            minWidth: 40,
            "& .MuiChip-label": { px: 1.25 },
          }}
        />

        <Box
          sx={{
            width: "1px",
            height: 20,
            backgroundColor: "#E2E8F0",
            flexShrink: 0,
          }}
        />

        <Box
          sx={{
            width: 16,
            height: 16,
            borderRadius: "4px",
            backgroundColor: hasColor ? row.hexCode : "transparent",
            border: "1.5px solid",
            borderColor: hasColor ? row.hexCode : "#CBD5E1",
            flexShrink: 0,
          }}
        />
        <Box sx={{ display: "flex" }}>
          <CustomTypography
            variant="body2"
            sx={{
              color: hasColor ? "#334155" : "#94A3B8",
              fontWeight: hasColor ? 500 : 400,
            }}
            style={{ fontSize: "0.8rem" }}
          >
            {row.colorName.toUpperCase() || "No color".toUpperCase()}
          </CustomTypography>
          {hasColor && (
            <CustomTypography
              variant="body3"
              sx={{
                fontFamily: "monospace",
                color: "#94A3B8",
              }}
              style={{ fontSize: "0.8rem" }}
            >
              , {row.hexCode.toUpperCase()}
            </CustomTypography>
          )}
        </Box>

        <Box
          sx={{
            width: "1px",
            height: 20,
            backgroundColor: "#E2E8F0",
            flexShrink: 0,
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <CustomTypography variant="body2" color="#64748B">
            Qty
          </CustomTypography>
          <Chip
            label={row.quantity}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: "0.8rem",
              backgroundColor: "#F0FDF4",
              color: "#16A34A",
              borderRadius: "6px",
              height: 22,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {hasEditPermissions && (
            <Tooltip title="Edit variant">
              <IconButton
                size="small"
                onClick={() => onEdit(row.id)}
                sx={{
                  color: "#64748B",
                  "&:hover": { backgroundColor: "#EEF2FF", color: "#4F46E5" },
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isDeleting ? "Deleting…" : "Delete variant"}>
            <span>
              <IconButton
                size="small"
                onClick={() => onDelete(row.id)}
                disabled={isDeleting}
                sx={{
                  color: "#64748B",
                  "&:hover": { backgroundColor: "#FEF2F2", color: "#DC2626" },
                }}
              >
                {isDeleting ? (
                  <CircularProgress size={15} sx={{ color: "#DC2626" }} />
                ) : (
                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* SKU strip */}
      <Box
        sx={{
          px: 2,
          py: 0.9,
          backgroundColor: isDuplicate ? "#FEF2F2" : "#FFFFFF",
          borderTop: "1px solid #F1F5F9",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <CustomTypography
          variant="body3"
          color="#94A3B8"
          sx={{ whiteSpace: "nowrap", fontSize: "0.7rem" }}
        >
          SKU
        </CustomTypography>
        <CustomTypography
          variant="body3"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.71rem",
            color: isDuplicate ? "#DC2626" : "#475569",
            letterSpacing: "0.02em",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={sku}
        >
          {sku || "–"}
        </CustomTypography>
        {isDuplicate && (
          <CustomTypography
            variant="body3"
            sx={{
              color: "#DC2626",
              fontSize: "0.67rem",
              whiteSpace: "nowrap",
              fontWeight: 600,
            }}
          >
            Duplicate
          </CustomTypography>
        )}
        <Tooltip title={isCopied ? "Copied!" : "Copy SKU"}>
          <span>
            <IconButton
              size="small"
              onClick={() => onCopySku(row.id)}
              disabled={!sku}
              sx={{ color: isCopied ? "#16A34A" : "#94A3B8", p: 0.4 }}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Audit info */}
      {(row.createdByAdminName ||
        row.createdByUserName ||
        row.updatedByAdminName ||
        row.updatedByUserName) && (
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderTop: "1px solid #F1F5F9",
            backgroundColor: "#FAFAFA",
            display: "flex",
            flexDirection: "row",
            gap: 0.25,
          }}
        >
          {(row.createdByAdminName || row.createdByUserName) && (
            <CustomTypography
              variant="body3"
              sx={{ fontSize: "0.68rem", color: "#94A3B8" }}
            >
              Created by{" "}
              <span style={{ color: "#64748B", fontWeight: 500 }}>
                {row.createdByAdminName || row.createdByUserName}
              </span>{" "}
              ({row.createdByAdminName ? "Admin" : "User"})
              {row.createdAt ? ` • ${formatAuditDate(row.createdAt)}` : ""}
            </CustomTypography>
          )}
          <Box
            sx={{
              marginX: 1,
              width: "1px",
              height: "20px",
              backgroundColor: colors.grey[500],
            }}
          />
          {(row.updatedByAdminName || row.updatedByUserName) && (
            <CustomTypography
              variant="body3"
              sx={{ fontSize: "0.68rem", color: "#94A3B8" }}
            >
              Updated by{" "}
              {[
                row.updatedByAdminName
                  ? `${row.updatedByAdminName} (Admin)`
                  : null,
                row.updatedByUserName
                  ? `${row.updatedByUserName} (User)`
                  : null,
              ]
                .filter(Boolean)
                .map((label, i) => (
                  <span key={i}>
                    {i > 0 && ", "}
                    <span style={{ color: "#64748B", fontWeight: 500 }}>
                      {label}
                    </span>
                  </span>
                ))}
              {row.updatedAt ? ` • ${formatAuditDate(row.updatedAt)}` : ""}
            </CustomTypography>
          )}
        </Box>
      )}

      {/* Delete error */}
      {deleteError && (
        <Box
          sx={{
            px: 2,
            pb: 1,
            pt: 0.25,
          }}
        >
          <CustomTypography
            variant="body3"
            sx={{ color: "#DC2626", fontWeight: 500 }}
          >
            {deleteError}
          </CustomTypography>
        </Box>
      )}
    </Box>
  );
};

/* ─── Edit Mode Card ─────────────────────────────────────────────────── */

interface EditVariantCardProps {
  row: {
    id: string;
    size: string;
    colorName: string;
    hexCode: string;
    quantity: number;
  };
  sizeOptionList: FilterValue[];
  hexDraft: string;
  sku: string;
  isDuplicate: boolean;
  isSaving: boolean;
  saveError?: string;
  isExistingVariant: boolean;
  isFullHex: (v: string) => boolean;
  hasEditPermissions: boolean;
  onRowChange: (
    id: string,
    key: keyof ProductVariantRowInput,
    value: string | number,
  ) => void;
  onHexTextChange: (id: string, value: string) => void;
  onHexTextBlur: (id: string) => void;
  onHexPickerChange: (id: string, hex: string) => void;
  onSave: (id: string) => void;
  onCancel: (id: string) => void;
}

const EditVariantCard: React.FC<EditVariantCardProps> = ({
  row,
  sizeOptionList,
  hexDraft,
  sku,
  isDuplicate,
  isSaving,
  saveError,
  isExistingVariant,
  isFullHex,
  hasEditPermissions,
  onRowChange,
  onHexTextChange,
  onHexTextBlur,
  onHexPickerChange,
  onSave,
  onCancel,
}) => {
  const hasColorName = row.colorName && row.colorName.trim().length > 0;
  const hasHexCode = isFullHex(row.hexCode || "");

  const canSave =
    !!row.size &&
    row.size.trim().length > 0 &&
    row.quantity > 0 &&
    !!sku &&
    !isDuplicate;

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        border: "1.5px solid #6366F1",
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: "0 0 0 3px rgba(99,102,241,0.08)",
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {/* Row 1: Size + Quantity */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <CustomTypography
              variant="body3"
              fontWeight={600}
              color="#64748B"
              sx={{ mb: 0.5, display: "block" }}
            >
              Size {isExistingVariant ? "" : "*"}
            </CustomTypography>
            {isExistingVariant ? (
              <Box
                sx={{
                  height: 38,
                  display: "flex",
                  alignItems: "center",
                  px: 1.5,
                  borderRadius: "10px",
                  border: "1px solid #E2E8F0",
                  backgroundColor: "#F8FAFC",
                  gap: 1,
                }}
              >
                <Chip
                  label={row.size}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.76rem",
                    backgroundColor: "#EEF2FF",
                    color: "#4F46E5",
                    borderRadius: "6px",
                    height: 24,
                    "& .MuiChip-label": { px: 1.25 },
                  }}
                />
                <CustomTypography
                  variant="body3"
                  color="#94A3B8"
                  sx={{ fontSize: "0.72rem" }}
                >
                  Cannot be changed
                </CustomTypography>
              </Box>
            ) : (
              <AppSearchDropdownMulti
                isMultiSelect={false}
                title="Select size"
                list={sizeOptionList}
                value={
                  row.size
                    ? sizeOptionList.filter((o) => o.value === row.size)
                    : []
                }
                local={true}
                handleSelect={(selectedItems) =>
                  onRowChange(row.id, "size", selectedItems[0]?.value || "")
                }
                inputHeight="38px"
                borderRadius="10px"
                minWidth="100%"
              />
            )}
          </Box>

          <Box sx={{ width: 140, flexShrink: 0 }}>
            <CustomTypography
              variant="body3"
              fontWeight={600}
              color="#64748B"
              sx={{ mb: 0.5, display: "block" }}
            >
              Quantity *
            </CustomTypography>
            <TextField
              size="small"
              fullWidth
              type="number"
              value={row.quantity}
              onChange={(e) => {
                const parsed = Number(e.target.value);
                onRowChange(
                  row.id,
                  "quantity",
                  isNaN(parsed) ? 0 : Math.max(0, Math.floor(parsed)),
                );
              }}
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& input": { py: 0.9, fontSize: "0.82rem" },
                },
              }}
            />
          </Box>
        </Box>

        {/* Row 2: Color Name + Hex Code */}
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <CustomTypography
              variant="body3"
              fontWeight={600}
              color="#64748B"
              sx={{ mb: 0.5, display: "block" }}
            >
              Color Name
            </CustomTypography>
            <TextField
              size="small"
              fullWidth
              placeholder="e.g. Navy Blue"
              value={row.colorName}
              onChange={(e) => onRowChange(row.id, "colorName", e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "& input": { py: 0.9, fontSize: "0.82rem" },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <CustomTypography
              variant="body3"
              fontWeight={600}
              color="#64748B"
              sx={{ mb: 0.5, display: "block" }}
            >
              Hex Code
            </CustomTypography>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
              <TextField
                size="small"
                value={hexDraft}
                onChange={(e) => onHexTextChange(row.id, e.target.value)}
                onBlur={() => onHexTextBlur(row.id)}
                placeholder="#RRGGBB"
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    "& input": {
                      py: 0.9,
                      fontSize: "0.8rem",
                      fontFamily: "monospace",
                    },
                  },
                }}
              />
              <Box
                component="label"
                htmlFor={`variant-color-${row.id}`}
                sx={{
                  width: 36,
                  height: 36,
                  mt: 0.1,
                  borderRadius: "8px",
                  border: "2px solid",
                  borderColor: hasHexCode ? row.hexCode : "#CBD5E1",
                  backgroundColor: hasHexCode ? row.hexCode : "#F8FAFC",
                  display: "inline-flex",
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "border-color 0.15s",
                }}
              />
              <input
                id={`variant-color-${row.id}`}
                type="color"
                value={hasHexCode ? row.hexCode : "#000000"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const nextHex = (e.target.value || "#000000").toUpperCase();
                  onHexPickerChange(row.id, nextHex);
                }}
                style={{
                  position: "absolute",
                  width: 1,
                  height: 1,
                  padding: 0,
                  border: 0,
                  margin: -1,
                  overflow: "hidden",
                  clip: "rect(0 0 0 0)",
                  whiteSpace: "nowrap",
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* SKU preview */}
      <Box
        sx={{
          mx: 2,
          mt: 1.5,
          mb: 1.5,
          px: 1.5,
          py: 0.9,
          backgroundColor: isDuplicate ? "#FEF2F2" : "#F8FAFC",
          border: `1px solid ${isDuplicate ? "#FECACA" : "#E2E8F0"}`,
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <CustomTypography
          variant="body3"
          color="#94A3B8"
          sx={{ whiteSpace: "nowrap", fontSize: "0.7rem" }}
        >
          SKU
        </CustomTypography>
        <CustomTypography
          variant="body3"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.72rem",
            color: isDuplicate ? "#DC2626" : "#334155",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {sku || "–"}
        </CustomTypography>
        {isDuplicate && (
          <CustomTypography
            variant="body3"
            sx={{
              color: "#DC2626",
              fontSize: "0.67rem",
              whiteSpace: "nowrap",
              fontWeight: 600,
            }}
          >
            Duplicate variant!
          </CustomTypography>
        )}
      </Box>

      {/* Save error */}
      {saveError && (
        <Box
          sx={{
            mx: 2,
            mb: 1.5,
            px: 1.5,
            py: 1,
            backgroundColor: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: "8px",
          }}
        >
          <CustomTypography
            variant="body3"
            sx={{ color: "#DC2626", fontWeight: 500 }}
          >
            {saveError}
          </CustomTypography>
        </Box>
      )}

      {/* Actions */}
      {hasEditPermissions && (
        <Box
          sx={{
            px: 2,
            pb: 1.75,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <AppButton
            label="Cancel"
            variant="secondary"
            onClick={() => onCancel(row.id)}
            customStyles={{
              minHeight: "34px",
              minWidth: "80px",
              borderRadius: "8px",
              fontSize: "0.78rem",
              backgroundColor: "#F1F5F9",
              color: "#475569",
              border: "1px solid #E2E8F0",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#E2E8F0", boxShadow: "none" },
            }}
          />

          <AppButton
            label={isSaving ? "Saving…" : "Save Variant"}
            startIcon={
              isSaving ? (
                <CircularProgress size={14} sx={{ color: "#FFFFFF" }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: "16px !important" }} />
              )
            }
            onClick={() => onSave(row.id)}
            disabled={!canSave || isSaving}
            customStyles={{
              minHeight: "34px",
              minWidth: "120px",
              borderRadius: "8px",
              fontSize: "0.78rem",
              fontWeight: 700,
              background: "linear-gradient(90deg, #6366F1 0%, #4F46E5 100%)",
              color: "#FFFFFF",
              boxShadow: "0 4px 12px rgba(79,70,229,0.25)",
              "&:hover": {
                background: "linear-gradient(90deg, #4F46E5 0%, #4338CA 100%)",
                boxShadow: "0 6px 16px rgba(67,56,202,0.3)",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default OrderProductVariantsDrawer;
